import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  FileText,
  Upload,
  Download,
  Settings,
  Users,
  History,
  Link2,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  Building2,
  Package,
  ClipboardEdit,
  Phone,
  AlertTriangle,
  UserPlus,
  Tags,
  KeyRound,
  ShieldCheck,
  ShieldAlert,
  Webhook,
  GitCompare,
  BookOpen,
  Shield,
  Sparkles,
  BarChart3,
  Target,
  Trash2
} from "lucide-react";
import FlacScopeIndicator from "./FlacScopeIndicator";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import GlobalSearch from "./GlobalSearch";
import { AssistantPanel } from "./AssistantPanel";
import ActiveImportsBadge from "./ActiveImportsBadge";

// Logo URLs
const EUROCALL_LOGO = "https://customer-assets.emergentagent.com/job_b17cad2c-20e2-43f4-94ab-e2a3873cd763/artifacts/ua87jms9_logo%20eurocall2.png";
const AQS_LOGO = "https://customer-assets.emergentagent.com/job_contract-hub-53/artifacts/0e2l56xd_334555.webp";

const NavItem = ({ item, sidebarOpen, indented = false }) => (
  <NavLink
    to={item.to}
    title={!sidebarOpen ? item.label : undefined}
    className={({ isActive }) =>
      `flex items-center gap-3 ${indented ? "px-2 py-2" : "px-3 py-2.5"} rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? "bg-[#1e3a8a] text-white"
          : "text-slate-600 hover:bg-[#1e3a8a]/10 hover:text-[#1e3a8a]"
      } ${!sidebarOpen && "justify-center px-2"}`
    }
    data-testid={`nav-${item.to.replace("/", "")}`}
  >
    <item.icon className="h-5 w-5 flex-shrink-0" />
    {sidebarOpen && <span className="flex-1">{item.label}</span>}
    {sidebarOpen && item.badgeCount > 0 && (
      <Badge
        variant="secondary"
        className="h-5 min-w-[1.25rem] px-1.5 text-xs bg-rose-600 text-white border-rose-700 font-semibold"
        data-testid={`nav-badge-${item.to.replace("/", "")}`}
      >
        {item.badgeCount > 99 ? "99+" : item.badgeCount}
      </Badge>
    )}
  </NavLink>
);

const Layout = () => {
  const { user, logout, hasRole, canExport, fieldAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Iter53 P20 — mobile drawer state (separat de colapsare desktop)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingEditRequests, setPendingEditRequests] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  // Iter27 — admin sidebar badge counts (gdpr_b2c_missing, sf_dlq_pending)
  const [sidebarCounts, setSidebarCounts] = useState({
    gdpr_b2c_missing: 0,
    sf_dlq_pending: 0,
  });

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Parola trebuie să aibă minim 8 caractere");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Parolele nu coincid");
      return;
    }
    setChangingPassword(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
      toast.success("Parolă schimbată cu succes");
      setShowChangePassword(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setChangingPassword(false);
    }
  };

  // Fetch pending edit-requests count for admin/backoffice/manager badge
  useEffect(() => {
    if (!user) return;
    if (!["admin", "backoffice", "manager"].includes(user.role)) return;
    const fetchCount = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + "/api";
        const res = await axios.get(`${API}/edit-requests?status=pending&limit=1`);
        setPendingEditRequests(res.data?.total || 0);
      } catch {
        setPendingEditRequests(0);
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, [user]);

  // Iter27 — fetch sidebar admin counts every 60s (only for admin/backoffice)
  useEffect(() => {
    if (!user) return;
    if (!["admin", "backoffice"].includes(user.role)) return;
    const fetchSidebar = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + "/api";
        const res = await axios.get(`${API}/admin/sidebar-counts`);
        setSidebarCounts({
          gdpr_b2c_missing: res.data?.gdpr_b2c_missing || 0,
          sf_dlq_pending: res.data?.sf_dlq_pending || 0,
        });
      } catch {
        // silent
      }
    };
    fetchSidebar();
    const id = setInterval(fetchSidebar, 60000);
    return () => clearInterval(id);
  }, [user]);

  // Iter53 P20 — închide drawer-ul mobil la schimbarea rutei (UX)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ---- Primary navigation (daily operational use) ----
  const mainNavItems = [
    {
      to: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      roles: ["admin", "backoffice", "manager", "agentie", "agent"],
    },
    {
      to: "/sales",
      icon: FileText,
      label: "Vânzări",
      roles: ["admin", "backoffice", "manager", "agentie", "agent"],
    },
    {
      to: "/clients",
      icon: Users,
      label: "Clienți",
      roles: ["admin", "backoffice", "manager", "agentie", "agent"],
    },
    {
      to: "/agents",
      icon: UserPlus,
      label: "Agenți",
      roles: ["admin", "backoffice", "manager"],
    },
    {
      // Iter53 P28 — Coadă Rework — dedicated page (replaces dashboard widget shortcut)
      to: "/rework",
      icon: AlertTriangle,
      label: "Coadă Rework",
      roles: ["admin", "backoffice", "manager", "agent", "agentie"],
    },
    {
      // Iter53 P25 — Monthly Objectives setting page (visible for roles that can read/write objectives)
      to: "/objectives",
      icon: Target,
      label: "Obiective",
      roles: ["admin", "backoffice", "manager", "agentie", "agent"],
    },
  ];

  // ---- Operational tasks (collapsed by default, focused on daily work for BO/Manager) ----
  const operationsNavItems = [
    {
      to: "/reconciliation",
      icon: AlertTriangle,
      label: "Reconciliere",
      roles: ["admin", "backoffice", "manager"],
    },
    {
      to: "/reconciliation/fuzzy",
      icon: ShieldCheck,
      label: "Reconciliere Fuzzy",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/import",
      icon: Upload,
      label: "Import Date",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/edit-requests",
      icon: ClipboardEdit,
      label: "Cereri Editare",
      roles: ["admin", "backoffice", "manager"],
      badgeCount: pendingEditRequests,
    },
    {
      to: "/agent-mappings",
      icon: Tags,
      label: "Aliasuri Agenți",
      roles: ["admin", "backoffice", "manager"],
    },
    {
      to: "/admin/commercial-ownership-mismatches",
      icon: GitCompare,
      label: "Mismatch Ownership",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/products",
      icon: Package,
      label: "Produse & Oferte",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/agencies",
      icon: Building2,
      label: "Agenții",
      roles: ["admin"],
    },
    {
      to: "/users",
      icon: Users,
      label: "Utilizatori",
      roles: ["admin", "backoffice"],
    },
  ];

  // ---- Tehnic / Sistem (collapsed by default, admin power-user) ----
  const adminNavItems = [
    {
      to: "/integrations",
      icon: Link2,
      label: "Integrări",
      roles: ["admin"],
    },
    {
      to: "/admin/sf-integration",
      icon: Webhook,
      label: "SF Live (Webhook)",
      roles: ["admin"],
      badgeKey: "sf_dlq_pending",
    },
    {
      to: "/admin/dictionaries",
      icon: BookOpen,
      label: "Dicționare",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/admin/classification-rules",
      icon: Sparkles,
      label: "Reguli clasificare",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/admin/field-access",
      icon: Shield,
      label: "Câmpuri & Acces",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/admin/flac-security",
      icon: ShieldAlert,
      label: "Securitate FLAC",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/admin/proposals",
      icon: Sparkles,
      label: "Propuneri Import",
      roles: ["admin"],
      badgeKey: "proposals_pending",
    },
    {
      to: "/admin/cleanup",
      icon: Trash2,
      label: "Curăță date test",
      roles: ["admin"],
    },
    {
      to: "/admin/field-parity-report",
      icon: BarChart3,
      label: "Raport Parity",
      roles: ["admin"],
    },
    {
      to: "/admin/data-reconciliation",
      icon: BarChart3,
      label: "Reconciliere date",
      roles: ["admin"],
    },
    {
      to: "/admin/energy-suppliers",
      icon: Building2,
      label: "Furnizori Energie",
      roles: ["admin"],
    },
    {
      to: "/custom-fields",
      icon: Settings,
      label: "Câmpuri Custom",
      roles: ["admin"],
    },
    {
      to: "/export",
      icon: Download,
      label: "Export",
      roles: ["admin"],
      permission: "export",
    },
    {
      to: "/audit-logs",
      icon: History,
      label: "Audit Logs",
      roles: ["admin", "backoffice"],
    },
    {
      to: "/documentation",
      icon: BookOpen,
      label: "Documentație",
      roles: ["admin", "backoffice", "manager"],
    },
  ];

  const filterByAccess = (item) => {
    if (!hasRole(item.roles)) return false;
    if (item.permission === "export" && !canExport()) return false;
    return true;
  };

  const filteredMainNav = mainNavItems.filter(filterByAccess);
  // Iter52 P19 — Operațiuni group (separate from Admin/Tehnic for cleaner UX)
  const filteredOperationsNav = operationsNavItems
    .filter(filterByAccess)
    .map((it) => {
      if (it.badgeKey && sidebarCounts[it.badgeKey] !== undefined) {
        return { ...it, badgeCount: sidebarCounts[it.badgeKey] };
      }
      return it;
    });
  // Iter27 — inject dynamic badge counts based on badgeKey before filter
  const filteredAdminNav = adminNavItems
    .filter(filterByAccess)
    .map((it) => {
      if (it.badgeKey && sidebarCounts[it.badgeKey] !== undefined) {
        return { ...it, badgeCount: sidebarCounts[it.badgeKey] };
      }
      return it;
    });

  // Auto-open Operațiuni when user navigates to one of its routes
  const isOperationsRouteActive = filteredOperationsNav.some((it) =>
    location.pathname.startsWith(it.to)
  );
  useEffect(() => {
    if (isOperationsRouteActive) setOperationsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const totalOperationsBadge = filteredOperationsNav.reduce(
    (sum, it) => sum + (it.badgeCount || 0),
    0
  );

  // Auto-open Administrare when user navigates to one of its routes
  const isAdminRouteActive = filteredAdminNav.some((it) =>
    location.pathname.startsWith(it.to)
  );
  useEffect(() => {
    if (isAdminRouteActive) setAdminOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const totalAdminBadge = filteredAdminNav.reduce(
    (sum, it) => sum + (it.badgeCount || 0),
    0
  );

  const getInitials = (name) => {
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleName = (role) => {
    const roleNames = {
      admin: "Administrator",
      backoffice: "Back Office",
      manager: "Manager",
      agentie: "Agenție",
      agent: "Agent"
    };
    return roleNames[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-700",
      backoffice: "bg-blue-100 text-blue-700",
      manager: "bg-purple-100 text-purple-700",
      agentie: "bg-amber-100 text-amber-700",
      agent: "bg-emerald-100 text-emerald-700"
    };
    return colors[role] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Iter53 P20 — Backdrop overlay (doar pe mobil când drawer-ul e deschis) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 h-screen bg-white border-r border-slate-200 transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        data-testid="app-sidebar"
      >
        {/* Logo */}
        <div className="flex h-20 items-center justify-between border-b border-slate-200 px-4">
          <div className={`flex items-center gap-2 ${!sidebarOpen && "justify-center w-full"}`}>
            {sidebarOpen ? (
              <img 
                src={EUROCALL_LOGO} 
                alt="EuroCall" 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#1e3a8a]">
                <Phone className="h-6 w-6 text-white" />
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={`hidden md:inline-flex ${!sidebarOpen && "md:hidden"}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Iter53 P20 — close button for mobile drawer */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-sidebar-close"
            aria-label="Închide meniul"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 p-4 overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
          {filteredMainNav.map((item) => (
            <NavItem key={item.to} item={item} sidebarOpen={sidebarOpen} />
          ))}

          {filteredOperationsNav.length > 0 && (
            <>
              <div className={`my-2 border-t border-slate-200 ${!sidebarOpen ? "mx-2" : ""}`} />
              <button
                type="button"
                onClick={() => {
                  if (!sidebarOpen) {
                    setSidebarOpen(true);
                    setOperationsOpen(true);
                  } else {
                    setOperationsOpen((v) => !v);
                  }
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isOperationsRouteActive
                    ? "text-[#1e3a8a]"
                    : "text-slate-600 hover:bg-[#1e3a8a]/10 hover:text-[#1e3a8a]"
                } ${!sidebarOpen && "justify-center px-2"}`}
                data-testid="nav-operations-group"
                aria-expanded={operationsOpen}
              >
                <ClipboardEdit className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">Operațiuni</span>
                    {totalOperationsBadge > 0 && !operationsOpen && (
                      <Badge
                        variant="secondary"
                        className="h-5 px-1.5 text-xs bg-amber-100 text-amber-800"
                        data-testid="nav-operations-group-badge"
                      >
                        {totalOperationsBadge > 99 ? "99+" : totalOperationsBadge}
                      </Badge>
                    )}
                    <ChevronRight
                      className={`h-4 w-4 text-slate-400 transition-transform ${
                        operationsOpen ? "rotate-90" : ""
                      }`}
                    />
                  </>
                )}
              </button>
              {sidebarOpen && operationsOpen && (
                <div className="ml-3 pl-2 border-l border-slate-200 flex flex-col gap-1 mt-1" data-testid="nav-operations-items">
                  {filteredOperationsNav.map((item) => (
                    <NavItem key={item.to} item={item} sidebarOpen={sidebarOpen} indented />
                  ))}
                </div>
              )}
            </>
          )}

          {filteredAdminNav.length > 0 && (
            <>
              {/* Separator */}
              <div className={`my-2 border-t border-slate-200 ${!sidebarOpen ? "mx-2" : ""}`} />

              {/* Administrare group header */}
              <button
                type="button"
                onClick={() => {
                  if (!sidebarOpen) {
                    // If sidebar is collapsed, expand it AND open the group
                    setSidebarOpen(true);
                    setAdminOpen(true);
                  } else {
                    setAdminOpen((v) => !v);
                  }
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isAdminRouteActive
                    ? "text-[#1e3a8a]"
                    : "text-slate-600 hover:bg-[#1e3a8a]/10 hover:text-[#1e3a8a]"
                } ${!sidebarOpen && "justify-center px-2"}`}
                data-testid="nav-admin-group"
                aria-expanded={adminOpen}
              >
                <ShieldCheck className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">Tehnic / Sistem</span>
                    {totalAdminBadge > 0 && !adminOpen && (
                      <Badge
                        variant="secondary"
                        className="h-5 px-1.5 text-xs bg-red-100 text-red-700"
                        data-testid="nav-admin-group-badge"
                      >
                        {totalAdminBadge > 99 ? "99+" : totalAdminBadge}
                      </Badge>
                    )}
                    <ChevronRight
                      className={`h-4 w-4 text-slate-400 transition-transform ${
                        adminOpen ? "rotate-90" : ""
                      }`}
                    />
                  </>
                )}
              </button>

              {/* Admin items (only when expanded sidebar + group open) */}
              {sidebarOpen && adminOpen && (
                <div className="ml-3 pl-2 border-l border-slate-200 flex flex-col gap-1 mt-1" data-testid="nav-admin-items">
                  {filteredAdminNav.map((item) => (
                    <NavItem key={item.to} item={item} sidebarOpen={sidebarOpen} indented />
                  ))}
                </div>
              )}
            </>
          )}
        </nav>

        {/* AQS Branding */}
        {sidebarOpen && (
          <div className="absolute bottom-24 left-0 right-0 px-4">
            <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
              <span>Powered by</span>
              <img 
                src={AQS_LOGO} 
                alt="AQS" 
                className="h-5 w-auto object-contain opacity-70"
              />
            </div>
          </div>
        )}

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200 p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex w-full items-center gap-3 rounded-lg p-2 hover:bg-slate-100 transition-colors ${
                  !sidebarOpen && "justify-center"
                }`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-[#1e3a8a] text-white text-sm font-medium">
                    {getInitials(user?.name || "U")}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {user?.name}
                      </p>
                      <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                        {getRoleName(user?.role)}
                      </Badge>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                {user?.agency_name && (
                  <p className="text-xs text-slate-500 mt-1">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    {user.agency_name}
                  </p>
                )}
                {/* Iter41 — FLAC scope indicator (clickable → modal with details) */}
                <div className="mt-2">
                  <FlacScopeIndicator fieldAccess={fieldAccess} />
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowChangePassword(true)}
                className="cursor-pointer"
                data-testid="change-password-btn"
              >
                <KeyRound className="mr-2 h-4 w-4" />
                Schimbă parola
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Deconectare
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`transition-all duration-300 ${
          sidebarOpen ? "md:pl-64" : "md:pl-20"
        }`}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-200 bg-white/80 px-4 md:px-6 backdrop-blur-md">
          {/* Iter53 P20 — Hamburger mobil mereu vizibil; pe desktop apare doar când sidebar e colapsat */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-sidebar-open"
            aria-label="Deschide meniul"
          >
            <Menu className="h-5 w-5" />
          </Button>
          {!sidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-expand"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <div className="flex-1 flex justify-center px-2">
            <GlobalSearch />
          </div>
          {/* Iter51 E27 — Persistent active imports indicator */}
          <ActiveImportsBadge />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAssistantOpen(true)}
            className="gap-1.5 border-sky-200 text-[#1e3a8a] hover:bg-sky-50"
            data-testid="assistant-open"
            aria-label="Deschide Asistentul CRM"
          >
            <Sparkles className="h-4 w-4 text-sky-500" />
            <span className="hidden sm:inline">Asistent</span>
          </Button>
          <AssistantPanel open={assistantOpen} onOpenChange={setAssistantOpen} />
          {/* EuroCall badge in header */}
          <div className="hidden lg:flex items-center gap-2 text-sm text-slate-500">
            <Phone className="h-4 w-4 text-[#1e3a8a]" />
            <span className="font-medium text-[#1e3a8a]">EuroCall CRM</span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Change password dialog */}
      <Dialog
        open={showChangePassword || !!user?.must_change_password}
        onOpenChange={(open) => {
          if (!open && !user?.must_change_password) {
            setShowChangePassword(false);
            setNewPassword("");
            setConfirmPassword("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {user?.must_change_password ? "Schimbă parola (obligatoriu)" : "Schimbă parola"}
            </DialogTitle>
            <DialogDescription>
              {user?.must_change_password
                ? "La primul login trebuie să îți setezi propria parolă."
                : "Setează o nouă parolă pentru contul tău."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Parolă nouă (min. 8 caractere)</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="input-new-password"
              />
            </div>
            <div>
              <Label>Confirmă parola</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="input-confirm-password"
              />
            </div>
          </div>
          <DialogFooter>
            {!user?.must_change_password && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setNewPassword("");
                  setConfirmPassword("");
                }}
              >
                Anulează
              </Button>
            )}
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              data-testid="btn-confirm-change-password"
            >
              {changingPassword ? "Se salvează..." : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
